// Эндпоинты подотчёта: пульт, карточка лица, выдачи, возвраты, передачи, заявки, чеки.
//
// Формы — зеркало contracts/openapi/bark-core.v1.yaml. Контракт правит только сессия ядра;
// поле, придуманное на фронте, даёт зелёную сборку и пустой экран у человека.
//
// Четыре особенности, из-за которых типы выглядят именно так.
//
// 1. **Все суммы — целые копейки.** Дробных величин в подотчёте нет вовсе: ни ставок, ни
//    часов, ни процентов. Значит ни одного поля-строки, в отличие от людей и ведомости.
// 2. **Остаток приходит ОТ СЕРВЕРА после каждой операции** (`balance_after`). Складывать
//    копейки в браузере нельзя: сложенное на клиенте разойдётся с сервером на первом сторно.
// 3. **Показатель сводки существует в трёх состояниях, разведённых типом.** «Ноль по
//    расчёту» и «считать нечем» — разные факты, и слипаться в серый прочерк они не имеют
//    права: по такой ячейке финансовый директор решает, платить или нет.
// 4. **Отрицательный остаток — норма.** Значит компания должна человеку. Показывается
//    цветом и подписью, ошибкой не считается: у заказчика такое есть.

import { request, requestUpload, type RequestOptions } from '@/shared/api/client';
import { queryString, type QueryValue } from '@/shared/api/query';
import type { ListResponse } from '@/shared/api/types';
import { ApiError } from '@/shared/errors/api-error';

// ── Отказ по дате ─────────────────────────────────────────────────────────────────────────

/** Машинный код отказа «дата операции в будущем». Общий для выдачи, возврата и заявки. */
export const КОД_ДАТА_В_БУДУЩЕМ = 'date_in_future';

/**
 * Текст отказа по дате — тот, что показывается ПОД полем даты, а не общей плашкой.
 *
 * Общая плашка внизу формы для этого случая не годится: человек смотрит на поле, в котором
 * ошибся, а сообщение живёт в другом конце панели. Разбор идёт по коду конверта, а не по
 * названию поля: имя поля пишется человеку словами и однажды изменится, код — договор.
 */
export function ошибкаДаты(error: unknown): string | undefined {
  if (!ApiError.is(error) || error.code !== КОД_ДАТА_В_БУДУЩЕМ) {
    return undefined;
  }
  const { message, hint } = error.envelope;
  return hint === undefined ? message : `${message}: ${hint}`;
}

// ── Замороженные наборы кодов ─────────────────────────────────────────────────────────────

/** contracts/enums/statuses.yaml, cash_method. */
export const CASH_METHODS = ['cash', 'transfer'] as const;
export type CashMethod = (typeof CASH_METHODS)[number];

/** Уровень подотчётного лица: получает из кассы либо получает от первого уровня. */
export const ACCOUNTABLE_LEVELS = ['primary', 'field'] as const;
export type AccountableLevel = (typeof ACCOUNTABLE_LEVELS)[number];

/** contracts/enums/statuses.yaml, expense_claim_status. */
export const CLAIM_STATUSES = [
  'draft',
  'submitted',
  'accepted',
  'rejected',
  'returned',
  'reversed',
] as const;
export type ClaimStatus = (typeof CLAIM_STATUSES)[number];

export function isClaimStatus(value: string): value is ClaimStatus {
  return (CLAIM_STATUSES as readonly string[]).includes(value);
}

/** contracts/enums/statuses.yaml, cash_transfer_status. `pending` — сумма в пути. */
export const TRANSFER_STATUSES = ['pending', 'accepted', 'declined', 'reversed'] as const;
export type TransferStatus = (typeof TRANSFER_STATUSES)[number];

// ── Баланс ────────────────────────────────────────────────────────────────────────────────

/**
 * Баланс подотчётного лица. Три корзины отдельными числами, а не одной суммой: заказчик
 * просил видеть и то, за что отчитались, и то, что ещё не проверено, и то, что отклонено.
 *
 * Все величины — целые копейки.
 */
export interface BalanceOut {
  /** Оборот: проведённые выдачи плюс входящие передачи. Возврат его НЕ уменьшает. */
  issued: number;
  written_off: number;
  confirmed: number;
  under_review: number;
  rejected: number;
  /** «Остаток крупным числом» на пульте. Может быть меньше нуля. */
  unconfirmed_balance: number;
  not_reported: number;
  /** Передано, но принимающий ещё не подтвердил: не списано ни у кого. */
  in_transit_out: number;
  in_transit_in: number;
  /** Остаток отрицательный. Не ошибка: человек потратил больше, чем получил. */
  company_owes: boolean;
  /** То же словами. Читает старший объекта, а не бухгалтер. */
  explanation: string;
}

export interface PersonOut {
  id: string;
  employee_id: string;
  employee_name: string;
  level: AccountableLevel;
  is_active: boolean;
  /** Порог подсветки на пульте. Не запрет: выдать сверх можно, это предупреждение. */
  soft_limit: number | null;
  over_soft_limit: boolean;
  balance: BalanceOut;
  /** Сколько дней висит самая старая неотчитанная выдача. null — висеть нечему. */
  oldest_unreported_days: number | null;
}

/**
 * Настройки подотчёта на карточке лица.
 *
 * `soft_limit: null` СНИМАЕТ порог; отсутствие ключа означает «не трогать». Разница нужна
 * затем, чтобы снятый порог можно было снять, а не только заменить другим числом.
 */
export interface PersonSettingsIn {
  soft_limit?: number | null;
  is_active?: boolean;
  basis?: string;
}

export interface PersonCardOut extends PersonOut {
  /** Каждый выданный рубль ровно в одном состоянии. Расхождение — событие, а не строка. */
  invariant_ok: boolean;
  invariant_text: string;
  invariant_delta: number;
  /**
   * Сколько ушло человеку личным долгом при снятии подотчётности, копейки.
   *
   * Приходит ТОЛЬКО в ответе на само снятие: это событие, а не свойство карточки. Пусто —
   * перевода не было (остатка не было либо подотчётность не выключали).
   */
  carried_to_debt?: number | null;
}

// ── Показатели сводки ─────────────────────────────────────────────────────────────────────

/**
 * Три состояния показателя, разведённые ТИПОМ, а не соглашением.
 *
 * - `value` — число есть;
 * - `zero` — ноль по расчёту, и обязательно сказано, почему ноль;
 * - `unknown` — считать нечем: источник ещё не построен. На экране это НЕ прочерк и НЕ ноль,
 *   а фраза «чем считается и чего не хватает». Ноль означал бы «недобора нет» и «платить
 *   нечего», и финансовый директор принял бы по этому решение.
 */
export type KpiOut =
  | {
      state: 'value';
      amount: number | null;
      count: number | null;
      reason: null;
      expected_from: null;
    }
  | {
      state: 'zero';
      amount: number | null;
      count: number | null;
      reason: string;
      expected_from: null;
    }
  | { state: 'unknown'; amount: null; count: null; reason: string; expected_from: string };

/**
 * Корзина давности неподтверждённого подотчёта: сколько денег висит столько-то дней.
 *
 * Общая сумма отвечает «сколько висит» и молчит про «давно ли». Корзины СХОДЯТСЯ в
 * `unconfirmed_total`: это ряд равнозначных величин, а ряд, который не сходится, человек
 * читает как ошибку расчёта.
 */
export interface AgeBucketOut {
  code: 'fresh' | 'week' | 'month' | 'stale';
  /** Подпись человеку: «до 7 дней», «8–14 дней». Собрана сервером. */
  title: string;
  amount: number;
  people: number;
}

export interface DashboardOut {
  unconfirmed_total: KpiOut;
  people_with_balance: KpiOut;
  debts_total: KpiOut;
  debts_to_withhold: KpiOut;
  understaffed_sites: KpiOut;
  understaffed_hours: KpiOut;
  payable_total: KpiOut;
  /** Разбивка неподтверждённого подотчёта по давности. Пусто — подотчёт роли не открыт. */
  unconfirmed_by_age: AgeBucketOut[];
  period_from: string;
  period_to: string;
  /** «Август 2026». Заголовок словами, а не «08.2026» (правило 9: календари подписаны). */
  period_title: string;
  generated_at: string;
}

// ── Справочник категорий ──────────────────────────────────────────────────────────────────

export interface CategoryOut {
  id: string;
  code: string;
  title: string;
  kind: 'expense' | 'transfer';
  /** Без получателя заявку не принять: долг заведётся в никуда. */
  needs_recipient: boolean;
  creates_debt: boolean;
  creates_debt_type: string | null;
  link_to_advance: boolean;
  sort_order: number;
  is_active: boolean;
}

// ── Выдачи, возвраты, передачи ────────────────────────────────────────────────────────────

export interface IssueOut {
  id: string;
  accountable_person_id: string;
  employee_name: string;
  legal_entity_id: string;
  number: string | null;
  amount: number;
  method: CashMethod;
  purpose: string | null;
  status: 'posted' | 'reversed';
  issued_at: string;
  /**
   * День выдачи по поясу объектов. Момент `issued_at` хранится в UTC, и дата, прочитанная из
   * него текстом, для выдачи в первые часы суток по местному времени оказывается вчерашней.
   * Необязательное: у записи, собранной без пояса, пусто, и тогда берётся день момента.
   */
  issued_on?: string | null;
  reversal_of_id: string | null;
  reversal_basis: string | null;
  created_at: string;
  /** Остаток человека ПОСЛЕ операции. Считает сервер. */
  balance_after: number | null;
  /** Порог превышен. Предупреждение, а не отказ: выдача уже проведена. */
  warning: string | null;
  idempotent_replay: boolean;
}

export interface ReturnOut {
  id: string;
  accountable_person_id: string;
  employee_name: string;
  amount: number;
  method: CashMethod;
  returned_at: string;
  created_at: string;
  balance_after: number | null;
  warning: string | null;
  idempotent_replay: boolean;
  /**
   * `reversed` — запись отменена, `posted` — действует.
   *
   * Сервер отдавал это поле с самого начала, а тип его не знал, и карточка рисовала
   * сторнированный возврат обычной строкой. То есть отменённые двадцать тысяч продолжали
   * выглядеть возвращёнными.
   */
  status: 'posted' | 'reversed';
  /** Заполнено у самой обратной записи: она ссылается на исходную. */
  reversal_of_id: string | null;
  /** Почему отменили. Видно в строке: через полгода это единственное объяснение. */
  reversal_basis: string | null;
}

export interface TransferOut {
  id: string;
  from_person_id: string;
  from_employee_name: string;
  to_person_id: string;
  to_employee_name: string;
  amount: number;
  status: TransferStatus;
  basis: string | null;
  transferred_at: string;
  confirmed_at: string | null;
  confirmed_by: string | null;
  declined_at: string | null;
  decline_reason: string | null;
  created_at: string;
  /** Остатки ОБЕИХ сторон после операции: их сумма обязана совпасть с суммой до. */
  sender_balance_after: number | null;
  receiver_balance_after: number | null;
  idempotent_replay: boolean;
}

// ── Заявки и чеки ─────────────────────────────────────────────────────────────────────────

export interface ReceiptOut {
  id: string;
  expense_claim_id: string;
  file_id: string;
  url: string;
  thumb_url: string;
  fiscal_fingerprint: string | null;
  fiscal_sum: number | null;
  fiscal_at: string | null;
  uploaded_at: string;
}

export interface ClaimOut {
  id: string;
  accountable_person_id: string;
  person_name: string;
  expense_category_id: string;
  category_code: string;
  category_title: string;
  legal_entity_id: string;
  site_id: string | null;
  recipient_employee_id: string | null;
  recipient_name: string | null;
  title: string;
  amount: number;
  spent_at: string | null;
  status: ClaimStatus;
  no_receipt: boolean;
  receipts: ReceiptOut[];
  submitted_at: string | null;
  accepted_at: string | null;
  decided_at: string | null;
  decided_by: string | null;
  reject_reason: string | null;
  supersedes_claim_id: string | null;
  reversal_of_id: string | null;
  reversal_basis: string | null;
  created_at: string;
  /** Что произошло помимо самой заявки: заведён долг, лёг аванс. Показывается сразу. */
  debt_id: string | null;
  debt_annulled: boolean;
  /** По аннулированному долгу уже удержано: эти деньги сами не вернутся. */
  debt_repaid: number | null;
  advance_linked: boolean;
  balance_after: number | null;
  /**
   * Предупреждение, а не отказ: заявка проведена. Сегодня оно одно — трата датирована
   * временем, которое закрытый расчёт получателя уже прошёл, и в его ведомость она не
   * попадёт никогда. Запретом это быть не может: задним числом заявки бывают законно.
   */
  warning: string | null;
  idempotent_replay: boolean;
}

export interface FileOut {
  id: string;
  kind: string;
  mime_type: string | null;
  size_bytes: number | null;
  original_name: string | null;
  uploaded_at: string;
  url: string;
  thumb_url: string;
  /** Тот же файл уже загружали. Не ошибка: повтор возвращает прежнюю запись. */
  deduplicated: boolean;
}

export interface BulkAcceptOut {
  accepted: number;
  accepted_amount: number;
  skipped: number;
  /** Идентификатор заявки → почему пропущена. Молча проглатывать пропуск нельзя. */
  skipped_reasons: Record<string, string>;
  debts_created: number;
}

// ── Запросы ───────────────────────────────────────────────────────────────────────────────

export interface PersonsQuery {
  legal_entity_id?: string;
  q?: string;
  active_only?: boolean;
  /**
   * Порядок: `age` — у кого висит дольше всех, сверху; `amount` — по убыванию остатка.
   * Не указан — сервер сортирует по давности: это стартовый экран финансового директора,
   * и его умолчание живёт на сервере, а не в вызывающем коде.
   */
  order?: 'age' | 'amount';
  /** Сборщик строки запроса принимает произвольный набор ключей; лишних мы не кладём. */
  [key: string]: QueryValue | readonly QueryValue[] | undefined;
}

export interface DashboardQuery {
  legal_entity_id?: string;
  /**
   * Период парой, а не по одному. Год без месяца сервер игнорирует целиком: молча подставить
   * к нему январь значило бы показать деньги не за тот период, и заметить это нечем.
   */
  year?: number;
  month?: number;
  [key: string]: QueryValue | readonly QueryValue[] | undefined;
}

export function fetchDashboard(
  params: DashboardQuery = {},
  options?: RequestOptions,
): Promise<DashboardOut> {
  return request<DashboardOut>(`/api/v1/accountable/dashboard${queryString(params)}`, options);
}

export function fetchPersons(
  params: PersonsQuery = {},
  options?: RequestOptions,
): Promise<ListResponse<PersonOut>> {
  return request<ListResponse<PersonOut>>(
    `/api/v1/accountable/persons${queryString(params)}`,
    options,
  );
}

export function fetchPerson(personId: string, options?: RequestOptions): Promise<PersonCardOut> {
  return request<PersonCardOut>(`/api/v1/accountable/persons/${personId}`, options);
}

export function updatePersonSettings(
  personId: string,
  body: PersonSettingsIn,
): Promise<PersonCardOut> {
  return request<PersonCardOut>(`/api/v1/accountable/persons/${personId}`, {
    method: 'PATCH',
    body,
  });
}

export function fetchMyAccountable(options?: RequestOptions): Promise<PersonCardOut> {
  return request<PersonCardOut>('/api/v1/accountable/me', options);
}

export function fetchCategories(options?: RequestOptions): Promise<ListResponse<CategoryOut>> {
  return request<ListResponse<CategoryOut>>('/api/v1/accountable/categories', options);
}

/**
 * Выдачи списком и сумма действующих выдач той же выборки.
 *
 * Сумму считает сервер: сложенная на экране разошлась бы с ним на первом сторно, а для выдач за
 * месяц это «Выдано за май» в сверке директора. Сторнированные в неё не входят даже при показе
 * «с учётом сторно». `period_*` пусты, когда месяц не запрашивали.
 */
export interface IssueListOut extends ListResponse<IssueOut> {
  total_amount: number;
  period_from: string | null;
  period_to: string | null;
}

/** Параметры списка выдач. Месяц и юрлицо необязательны: без них список прежний. */
export interface IssuesQuery {
  [key: string]: QueryValue | readonly QueryValue[] | undefined;
  person_id?: string;
  with_reversed?: boolean;
  /** Месяц выдач парой `year` и `month`. Год без месяца сервер не применяет. */
  year?: number;
  month?: number;
  legal_entity_id?: string;
  /**
   * Месяц не задан человеком: если в запрошенном выдач нет, сервер отвечает последним более
   * ранним, где они есть. Какой месяц пришёл, видно по `period_from` в ответе. Месяц, выбранный
   * в адресе, флагом не сопровождается никогда: явный выбор главнее подсказки.
   */
  latest_if_empty?: boolean;
}

export function fetchIssues(
  params: IssuesQuery = {},
  options?: RequestOptions,
): Promise<IssueListOut> {
  return request<IssueListOut>(`/api/v1/accountable/issues${queryString(params)}`, options);
}

export function fetchReturns(
  params: { person_id?: string; with_reversed?: boolean } = {},
  options?: RequestOptions,
): Promise<ListResponse<ReturnOut>> {
  return request<ListResponse<ReturnOut>>(
    `/api/v1/accountable/returns${queryString(params)}`,
    options,
  );
}

export function fetchTransfers(
  params: { person_id?: string; status?: string } = {},
  options?: RequestOptions,
): Promise<ListResponse<TransferOut>> {
  return request<ListResponse<TransferOut>>(
    `/api/v1/accountable/transfers${queryString(params)}`,
    options,
  );
}

export interface ClaimsQuery {
  [key: string]: QueryValue | readonly QueryValue[] | undefined;
  person_id?: string;
  status?: string[];
  no_receipt?: boolean;
  amount_from?: number;
  /** Только круглые суммы, кратные тысяче рублей. Справочный отбор, а не обвинение. */
  round_only?: boolean;
  category_id?: string;
  legal_entity_id?: string;
  with_reversed?: boolean;
  q?: string;
}

export function fetchClaims(
  params: ClaimsQuery = {},
  options?: RequestOptions,
): Promise<ListResponse<ClaimOut>> {
  return request<ListResponse<ClaimOut>>(
    `/api/v1/accountable/claims${queryString(params)}`,
    options,
  );
}

export function fetchClaim(claimId: string, options?: RequestOptions): Promise<ClaimOut> {
  return request<ClaimOut>(`/api/v1/accountable/claims/${claimId}`, options);
}

// ── Действия ──────────────────────────────────────────────────────────────────────────────

export interface IssueIn {
  accountable_person_id: string;
  legal_entity_id: string;
  amount: number;
  method: CashMethod;
  purpose?: string | null;
  /**
   * Когда выдали, ISO-датой «2026-08-15». Пусто либо отсутствует — сервер ставит «сейчас».
   *
   * Нужно для переноса того, что уже случилось: заказчик заводит в программу остатки прошлых
   * месяцев, и без настоящей даты выдачи давность на пульте у всех начинается с нуля, то есть
   * главный вопрос финансового директора «давно ли висит» остаётся без ответа.
   */
  issued_at?: string | null;
}

export function registerIssue(body: IssueIn, idempotencyKey: string): Promise<IssueOut> {
  return request<IssueOut>('/api/v1/accountable/issues', {
    method: 'POST',
    body,
    idempotencyKey,
  });
}

export function reverseIssue(issueId: string, basis: string): Promise<IssueOut> {
  return request<IssueOut>(`/api/v1/accountable/issues/${issueId}/reverse`, {
    method: 'POST',
    body: { basis },
  });
}

export function reverseReturn(returnId: string, basis: string): Promise<ReturnOut> {
  return request<ReturnOut>(`/api/v1/accountable/returns/${returnId}/reverse`, {
    method: 'POST',
    body: { basis },
  });
}

export interface ReturnIn {
  accountable_person_id: string;
  amount: number;
  method: CashMethod;
  /** Когда вернули, ISO-датой. Пусто — сервер ставит «сейчас». */
  returned_at?: string | null;
}

export function registerReturn(body: ReturnIn, idempotencyKey: string): Promise<ReturnOut> {
  return request<ReturnOut>('/api/v1/accountable/returns', {
    method: 'POST',
    body,
    idempotencyKey,
  });
}

export interface TransferIn {
  from_person_id?: string;
  to_person_id: string;
  amount: number;
  basis?: string | null;
  /** Финотдел оформляет уже случившуюся передачу за обоих: одно действие, сценарий 56. */
  confirm_immediately?: boolean;
}

export function startTransfer(body: TransferIn, idempotencyKey: string): Promise<TransferOut> {
  return request<TransferOut>('/api/v1/accountable/transfers', {
    method: 'POST',
    body,
    idempotencyKey,
  });
}

export function confirmTransfer(transferId: string, idempotencyKey: string): Promise<TransferOut> {
  return request<TransferOut>(`/api/v1/accountable/transfers/${transferId}/confirm`, {
    method: 'POST',
    idempotencyKey,
  });
}

export function declineTransfer(transferId: string, reason: string): Promise<TransferOut> {
  return request<TransferOut>(`/api/v1/accountable/transfers/${transferId}/decline`, {
    method: 'POST',
    body: { reason },
  });
}

export interface ClaimIn {
  accountable_person_id?: string;
  expense_category_id: string;
  legal_entity_id: string;
  title: string;
  amount: number;
  /** Когда потратили, ISO-датой. Пусто — сервер считает днём подачи. */
  spent_at?: string | null;
  site_id?: string | null;
  recipient_employee_id?: string | null;
  no_receipt?: boolean;
  receipt_file_ids?: string[];
}

export function createClaim(body: ClaimIn, idempotencyKey: string): Promise<ClaimOut> {
  return request<ClaimOut>('/api/v1/accountable/claims', {
    method: 'POST',
    body,
    idempotencyKey,
  });
}

export function acceptClaim(claimId: string): Promise<ClaimOut> {
  return request<ClaimOut>(`/api/v1/accountable/claims/${claimId}/accept`, { method: 'POST' });
}

export function rejectClaim(claimId: string, reason: string): Promise<ClaimOut> {
  return request<ClaimOut>(`/api/v1/accountable/claims/${claimId}/reject`, {
    method: 'POST',
    body: { reason },
  });
}

export function returnClaim(claimId: string, reason: string): Promise<ClaimOut> {
  return request<ClaimOut>(`/api/v1/accountable/claims/${claimId}/return`, {
    method: 'POST',
    body: { reason },
  });
}

export function reverseClaim(claimId: string, basis: string): Promise<ClaimOut> {
  return request<ClaimOut>(`/api/v1/accountable/claims/${claimId}/reverse`, {
    method: 'POST',
    body: { basis },
  });
}

export function bulkAcceptClaims(claimIds: string[]): Promise<BulkAcceptOut> {
  return request<BulkAcceptOut>('/api/v1/accountable/claims/bulk-accept', {
    method: 'POST',
    body: { claim_ids: claimIds },
  });
}

/**
 * Загрузка файла: снимка чека либо подтверждения перевода.
 *
 * Отдельным запросом ДО создания записи: так человек видит, что файл доехал, ещё до того как
 * отправит саму заявку, и при обрыве теряется загрузка, а не отчёт.
 *
 * Вид — параметр, а не зашитое слово: сервер принимает `receipt`, `document` и
 * `payment_proof`, и вторая функция «то же самое, но с другим видом» разошлась бы с первой на
 * первой же правке предела размера или списка форматов.
 */
export function uploadFile(
  file: File,
  kind: 'receipt' | 'document' | 'payment_proof' = 'receipt',
): Promise<FileOut> {
  const form = new FormData();
  form.append('file', file);
  form.append('kind', kind);
  return requestUpload<FileOut>('/api/v1/files', form);
}
